import React, {useRef, useState} from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import {Button, Input, Toaster} from '@dized/ui';

import UserActions from '../../actions/UserActions';
import {generateLoginUrl} from '../../common/utils/urlUtils';

import background from '../Login/dized-logo-background.svg';
import logo from '../Login/dized.svg';
import {CREATE_TEST_ORGANIZATION_PATH} from '../Portal';

const Background = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  background: url(${background}) no-repeat 50% 5% #e3fbfe;
  align-items: center;
`;

const Container = styled.div`
  width: 90%;
  min-width: 500px;
  margin: 10px auto;
  padding: 10px 40px 20px;
  background-color: white;
  align-items: center;
  justify-content: center;
`;

const Title = styled.div`
  text-align: center;
  padding: 40px;
`;

const Paragraph = styled.div`
  margin: 5px auto;
`;

const StyledButton = styled(Button)`
  border: solid;
  border-color: transparent;

  &:focus {
    border-color: black;
  }
`;

const StyledInput = styled(Input)`
  border: solid;
  border-color: transparent;

  &:focus {
    border-color: black;
  }
`;

/**
 * If the Enter key is pressed, focus the given ref.
 * @param {KeyboardEvent} event - The keyboard event.
 * @param {React.MutableRefObject<HTMLInputElement | null>} ref - The reference to the input element.
 * @returns {void}
 */
const changeFocus = (event, ref) => {
  if (event.target.value !== '' && event.key === 'Enter') ref.current.focus();
};

const mapStateToProps = (store) => ({
  invalidSession: store.session.invalidSession,
});

const mapDispatchToProps = (dispatch) => ({
  ...UserActions.bindActions(dispatch),
});

/**
 * Register component for handling user registration.
 *
 * This component allows users to register by filling in their first name, last name, email,
 * and password. The input fields are automatically focused when the Enter key is pressed.
 * Once all fields are filled, the user can submit the form to create a new account. Upon
 * successful registration, a success message is displayed, and the user is redirected to
 * the test organization creation page. In case of any error during registration, an
 * appropriate error message is shown.
 *
 * @param {Object} props - Properties passed to the component.
 * @param {Object} props.history - React Router history object.
 * @param {Function} props.history.replace - Function to replace the current route.
 * @param {boolean} props.invalidSession - Flag indicating if the current session is invalid.
 * @param {Function} props.LOGOUT - Function to log out the user.
 * @param {Function} props.POST_REGISTER - Function to register a new user.
 * @returns {JSX.Element} The rendered component.
 */
const Register = ({
  history: {replace},
  invalidSession,
  LOGOUT,
  POST_REGISTER,
}) => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const lastNameRef = useRef();
  const [email, setEmail] = useState('');
  const emailRef = useRef();
  const [password, setPassword] = useState('');
  const passwordRef = useRef();
  const buttonRef = useRef();

  return (
    <Background>
      <Container>
        <Title>
          <img src={logo} alt="Dized" />
          <h2>Register New User</h2>
        </Title>
        <Paragraph>First Name</Paragraph>
        <Paragraph>
          <StyledInput
            onChange={(e) => setFirstName(e.target.value)}
            onKeyPress={(event) => changeFocus(event, lastNameRef)}
            value={firstName}
          />
        </Paragraph>
        <Paragraph>Last Name</Paragraph>
        <Paragraph>
          <StyledInput
            onChange={(e) => setLastName(e.target.value)}
            onKeyPress={(event) => changeFocus(event, emailRef)}
            ref={lastNameRef}
            value={lastName}
          />
        </Paragraph>
        <Paragraph>Email</Paragraph>
        <Paragraph>
          <StyledInput
            onChange={(e) => setEmail(e.target.value)}
            onKeyPress={(event) => changeFocus(event, passwordRef)}
            ref={emailRef}
            type="email"
            value={email}
          />
        </Paragraph>
        <Paragraph>Password</Paragraph>
        <Paragraph>
          <StyledInput
            onChange={(e) => setPassword(e.target.value)}
            onKeyPress={(event) => changeFocus(event, buttonRef)}
            ref={passwordRef}
            type="password"
            value={password}
          />
        </Paragraph>
        <StyledButton
          disabled={
            firstName === '' ||
            lastName === '' ||
            email === '' ||
            password === ''
          }
          onClick={() => {
            return (
              Promise.resolve()
                .then(() =>
                  POST_REGISTER({
                    email,
                    firstname: firstName,
                    lastname: lastName,
                    password,
                    username: `${firstName} ${lastName}`,
                  }),
                )
                .then(() =>
                  Toaster.success(`Account for '${email}' has been created.`),
                )
                // NOTE: make sure current session is invalidated and then redirect
                //       to the test organization creation page via the login page.
                .then(() => {
                  if (!invalidSession) return LOGOUT();
                })
                .then(() =>
                  replace(
                    generateLoginUrl(
                      CREATE_TEST_ORGANIZATION_PATH,
                      `/${CREATE_TEST_ORGANIZATION_PATH}?newuser`, // flag indicates new user to login page
                    ),
                  ),
                )
                .catch((error) => {
                  if (error.response)
                    Toaster.error(error.response.data.error.message);
                  else if (error.message) Toaster.error(error.message);
                  else Toaster.error('Something went wrong.');
                })
            );
          }}
          ref={buttonRef}
        >
          Register
        </StyledButton>
      </Container>
    </Background>
  );
};

Register.propTypes = {
  history: PropTypes.shape({
    replace: PropTypes.func.isRequired,
  }).isRequired,
  invalidSession: PropTypes.bool.isRequired,
  LOGOUT: PropTypes.func.isRequired,
  POST_REGISTER: PropTypes.func.isRequired,
};

export default connect(mapStateToProps, mapDispatchToProps)(Register);
